import { theme as baseTheme } from '@chakra-ui/react'

// Utility function to adjust color shades
const adjustColorShade = (hexColor: string, factor: number): string => {
  // Remove the hash at the beginning if it exists
  const hex = hexColor.replace(/^#/, '')

  // Parse the hex string to RGB values
  let r = Number.parseInt(hex.slice(0, 2), 16)
  let g = Number.parseInt(hex.slice(2, 4), 16)
  let b = Number.parseInt(hex.slice(4, 6), 16)

  // Adjust the RGB values by the factor
  // factor > 1 makes the color lighter, factor < 1 makes it darker
  r = Math.min(255, Math.max(0, Math.round(r * factor)))
  g = Math.min(255, Math.max(0, Math.round(g * factor)))
  b = Math.min(255, Math.max(0, Math.round(b * factor)))

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

const primary = {
  main: '#623875',
  light: '#cec1d4',
  dark: '#462853',
  100: '#cec1d4',
  300: '#cec1d4',
  500: '#623875',
  600: '#462853',
}

const greys = {
  50: '#f4f4f6',
  100: '#e7e8eb',
  200: '#b4b6c1',
  300: '#8f93a3',
  400: '#5c6278',
  500: '#3d435e',
  600: '#0c1436',
  700: '#0b1231',
  white: '#ffffff',
}

const other = {
  informationFocus: '#0671e0',
  dividerStroke: '#b4b6c1',
  glassStroke: '#ffffff',
}

const signal = {
  success: '#33c4b3',
  dangerError: '#ff375b',
  positive: '#00884b',
  negative: '#B52741',
  caution: '#ff9903',
  cautionLight: '#ffdfb1',
  cautionDark: '#b56d02',
  dangerErrorLight: '#ffc1cc',
  dangerErrorDark: '#b52741',
  successLight: '#c0ede7',
  successDark: '#248b7f',
}

const danger = {
  400: signal.dangerErrorLight,
  500: signal.dangerError,
  600: signal.dangerErrorDark,
}

const productAccent = {
  contactCentreLight: '#dbacf5',
  virtualAgentsMain: '#0f78c7',
  virtualAgentsLight: '#60ade7',
  conversationIntelligenceMain: '#ff9903',
  conversationIntelligenceLight: '#fff3eb',
  workforceManagementMain: '#00c06a',
  workforceManagementLight: '#b0ebd1',
  caseManagementMain: '#c79ddf',
  caseManagementLight: '#f4e5fc',
  agentCopilotMain: '#33c4b3',
  agentCopilotLight: '#c0ede7',
}

const graph = {
  primary: {
    300: adjustColorShade('#216d97', 1.3), // Lighter shade
    500: '#216d97', // Base color
    600: adjustColorShade('#216d97', 0.8), // Darker shade
  },
  secondary: {
    300: adjustColorShade('#ff9903', 1.3), // Lighter shade
    500: '#ff9903', // Base color
    600: adjustColorShade('#ff9903', 0.8), // Darker shade
  },
  tertiary: {
    300: adjustColorShade('#549288', 1.3), // Lighter shade
    500: '#549288', // Base color
    600: adjustColorShade('#549288', 0.8), // Darker shade
  },
  quarternary: {
    300: adjustColorShade('#dbacf5', 1.3), // Lighter shade
    500: '#dbacf5', // Base color
    600: adjustColorShade('#dbacf5', 0.8), // Darker shade
  },
  quinary: {
    300: adjustColorShade('#ff375b', 1.3), // Lighter shade
    500: '#ff375b', // Base color
    600: adjustColorShade('#ff375b', 0.8), // Darker shade
  },
  senary: {
    300: adjustColorShade('#e88b03', 1.3), // Lighter shade
    500: '#e88b03', // Base color
    600: adjustColorShade('#e88b03', 0.8), // Darker shade
  },
  septenary: {
    300: adjustColorShade('#a1000b', 1.3), // Lighter shade
    500: '#a1000b', // Base color
    600: adjustColorShade('#a1000b', 0.8), // Darker shade
  },
  octonary: {
    300: adjustColorShade('#0c1436', 1.3), // Lighter shade
    500: '#0c1436', // Base color
    600: adjustColorShade('#0c1436', 0.8), // Darker shade
  },
  nonary: {
    300: adjustColorShade('#b5ada7', 1.3), // Lighter shade
    500: '#b5ada7', // Base color
    600: adjustColorShade('#b5ada7', 0.8), // Darker shade
  },
  denary: {
    300: adjustColorShade('#8c5402', 1.3), // Lighter shade
    500: '#8c5402', // Base color
    600: adjustColorShade('#8c5402', 0.8), // Darker shade
  },
}

const widgetBackground = {
  blue: '#e7f3fb',
  sand: '#e7e8eb',
  green: '#C0EDE7',
  yellow: '#FFDFB1',
  red: '#FFC1CC',
}

const productgradients = {
  conversationalintelligence:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), radial-gradient(92.76% 73.33% at -2% -4.67%, rgba(255, 153, 3, 0.40) 0%, rgba(255, 153, 3, 0.00) 100%), radial-gradient(173.95% 133.06% at 110% 111.33%, rgba(255, 153, 3, 0.30) 0%, rgba(255, 153, 3, 0.00) 100%), #FFF',
  virtualAgents:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), radial-gradient(92.76% 73.33% at -2% -4.67%, #0F78C7 0%, rgba(15, 120, 199, 0.00) 100%), radial-gradient(173.95% 133.06% at 110% 111.33%, rgba(96, 173, 231, 0.90) 0%, rgba(96, 173, 231, 0.00) 100%), linear-gradient(0deg, #B5D9F4 0%, #60ADE7 66%)',
}

const gradients = {
  aiGradientWeak:
    'linear-gradient(260deg, rgba(255, 153, 3, 0.00) 23.47%, rgba(255, 55, 91, 0.06) 47.05%, rgba(51, 196, 179, 0.00) 78.99%), linear-gradient(293deg, rgba(255, 55, 91, 0.00) 17.32%, rgba(255, 153, 3, 0.03) 48.33%, rgba(51, 196, 179, 0.06) 61.28%, rgba(51, 196, 179, 0.00) 85.76%), linear-gradient(270deg, #FFF 0%, #FAFAFA 100%)',
  aiGradientStrong:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(180deg, rgba(255, 255, 255, 0.00) 43.85%, rgba(255, 255, 255, 0.75) 100%), radial-gradient(41.08% 58.46% at 50.27% 0%, rgba(255, 153, 3, 0.25) 0%, rgba(255, 153, 3, 0.00) 100%), radial-gradient(85.91% 84.32% at 0% 0%, rgba(51, 196, 179, 0.50) 0%, rgba(51, 196, 179, 0.00) 100%), radial-gradient(112.44% 70.52% at 100% 0%, rgba(219, 172, 245, 0.75) 0%, rgba(219, 172, 245, 0.00) 100%), #F4E5FC',
  aiStrokeText: 'linear-gradient(0deg, #ff9cac 0%, #fecc82 49%, #9ce2d8 100%)',
  dark: 'linear-gradient(0deg, rgba(98, 56, 117, 0.50) 0%, rgba(98, 56, 117, 0.50) 100%), radial-gradient(109.42% 85.6% at -2% -4.67%, rgba(51, 196, 179, 0.75) 0%, rgba(51, 196, 179, 0.00) 100%), radial-gradient(167.07% 153.23% at 130.67% 154.67%, rgba(255, 153, 3, 0.50) 0.19%, rgba(255, 153, 3, 0.25) 65%, rgba(255, 153, 3, 0.00) 100%), linear-gradient(91deg, #462853 0%, #623875 35%, #C79DDF 100%)',
  light:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), radial-gradient(92.76% 73.33% at -2% -4.67%, #623875 0%, rgba(98, 56, 117, 0.00) 100%), radial-gradient(173.95% 133.06% at 110% 111.33%, rgba(51, 196, 179, 0.90) 0%, rgba(51, 196, 179, 0.00) 100%), linear-gradient(0deg, #EED9FA 0%, #DBACF5 66%)',
  glass:
    'linear-gradient(114deg, rgba(255, 255, 255, 0.60) 2.72%, rgba(255, 255, 255, 0.80) 100.25%)',
  gold: 'linear-gradient(0deg, #ffd08b 0%, #e88b03 100%)',
}

const graphGradients = {
  primaryFill: 'linear-gradient(0deg, #216d97 0%, #ffffff 100%)',
  primaryStroke: 'linear-gradient(0deg, #a9c6d7 0%, #216d97 100%)',
  secondaryFill: 'linear-gradient(0deg, #ff9903 0%, #ffffff 100%)',
  secondaryStroke: 'linear-gradient(0deg, #ffd08b 0%, #ff9903 100%)',
  tertiaryFill: 'linear-gradient(0deg, #549288 0%, #ffffff 100%)',
  tertiaryStroke: 'linear-gradient(0deg, #8ab4ad 0%, #549288 100%)',
  quaternaryFill: 'linear-gradient(0deg, #dbacf5 0%, #ffffff 100%)',
  quaternaryStroke: 'linear-gradient(0deg, #eed9fa 0%, #dbacf5 100%)',
  quinaryFill: 'linear-gradient(0deg, #ff375b 0%, #ffffff 100%)',
  quinaryStroke: 'linear-gradient(0deg, #ffa3b4 0%, #ff375b 100%)',
  senaryFill: 'linear-gradient(0deg, #e88b03 0%, #ffffff 100%)',
  senaryStroke: 'linear-gradient(0deg, #ffd08b 0%, #e88b03 100%)',
  septenaryFill: 'linear-gradient(0deg, #a1000b 0%, #ffffff 100%)',
  septenaryStroke: 'linear-gradient(0deg, #f28a91 0%, #a1000b 100%)',
  octonaryFill: 'linear-gradient(0deg, #0c1436 0%, #ffffff 100%)',
  octonaryStroke: 'linear-gradient(0deg, #8f93a3 0%, #0c1436 100%)',
  nonaryFill: 'linear-gradient(0deg, #b5ada7 0%, #ffffff 100%)',
  nonaryStroke: 'linear-gradient(0deg, #e8ddd6 0%, #b5ada7 100%)',
  denaryFill: 'linear-gradient(0deg, #8c5402 0%, #ffffff 100%)',
  denaryStroke: 'linear-gradient(0deg, #ffad35 0%, #8c5402 100%)',
}

const segments = {
  primary: graph.primary,
  secondary: graph.secondary,
  tertiary: graph.tertiary,
  quaternary: graph.quarternary,
  quinary: graph.quinary,
  senary: graph.senary,
}

const secondary = greys

const brand = {
  primary: primary.main,
  secondary: secondary[200],
}

const typography = {
  subtitle: greys[300],
}

const colors = {
  ...baseTheme.colors,
  // brand theme
  brand,
  // hues
  primary,
  greys,
  secondary,
  danger,
  other,
  productAccent,
  graph,
  signal,
  widgetBackground,
  productgradients,
  gradients,
  graphGradients,
  // data segments
  segmentPrimary: graph.primary[500],
  segmentSecondary: graph.secondary[500],
  segmentTertiary: graph.tertiary[500],
  segmentQuaternary: graph.quarternary[500],
  segmentQuinary: graph.quinary[500],
  segmentSenary: graph.senary[500],
  segments,

  ...typography,
  border: {
    light: greys[100],
  },
  outlineColor: `${primary.main}33`,

  cases: {
    inbound: 'linear-gradient(131deg, #FBF4E5 7.57%, #FEEBC8 86.83%)',
    outbound: 'linear-gradient(107deg, #EEF7FB 10.21%, #E2F0F9 66.35%)',
    autoReply: 'linear-gradient(243deg, #FAFAFA 11.91%, #F5F5F5 81.22%)',
  },
  /*
    TODO:
    Component theming belongs in the components folder.
    See: `/src/components` and https://chakra-ui.com/docs/theming/component-style
  */
  workspace: {
    bodyBackground: greys.white,
    contentBackground: gradients.glass,
    contentBorder: greys.white,
    sidebar: {
      darkbg: gradients.dark,
      lightbg: productgradients.conversationalintelligence,
      text: greys[600],
      headerText: greys[400],
    },
  },
  card: {
    bg: greys.white,
    header: {
      title: greys[600],
      subtitle: greys[400],
    },
    borderColor: greys[100],
  },

  // Contextual colors
  success: signal.success,
  warning: signal.caution,
  warningDark: signal.cautionDark,
  warningLight: signal.cautionLight,
  info: greys[300],
  //used for showing color on positive number
  positive: signal.positive,
  //used for showing color on negative number
  negative: signal.negative,

  // Text
  textMuted: greys[400],
  text: greys[600],

  // open id providers
  google: '#4285F4',
  microsoft: '#DC3E15',
}

export default colors
